/**
 * Remove keys from an object where the value is null, undefined, or an empty string.
 *
 * @template T
 * @param {T} object The object to clean.
 * @returns {Cleaned<T>} A new object without any null, undefined, or empty string values.
 */
export function clean(object) {
  return Object.entries(object).reduce((result, [key, value]) => {
    if ((value !== '')
      && (value !== null)
      && (value !== undefined)) {
      // eslint-disable-next-line no-param-reassign -- clear code
      result[key] = typeof value === 'object' && !Array.isArray(value) ? clean(value) : value;
    }
    return result;
  }, {});
}
// eslint-disable-next-line no-promise-executor-return -- used for delaying
export const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

/**
 * Remove slashes from the end of an URL.
 *
 * @param {string} url  An URL to normalize.
 */
export const normalizeApiUrl = (url) => (url ? url.replace(/\/+$/, '') : '');

/**
 * From `T` remove a set of properties by value matching `ValueType`
 *
 * @template T, ValueType
 * @typedef {Pick<T, {[K in keyof T]-?: T[K] extends ValueType ? never : K}[keyof T]} OmitByValue
 */

/**
 * From `T` remove properties with a value of null, undefined, or an empty string.
 *
 * @template T
 * @typedef {Partial<OmitByValue<T, null | undefined>>} Cleaned
 */

export const countryToRegionMap = {
  AR: 'canlac',
  AT: 'emea',
  AU: 'japa',
  BH: 'emea',
  CA: 'canlac',
  CN: 'japa',
  DE: 'emea',
  ES: 'emea',
  FI: 'emea',
  FR: 'emea',
  GB: 'emea',
  HK: 'japa',
  IN: 'japa',
  IT: 'emea',
  JP: 'japa',
  MX: 'canlac',
  MY: 'japa',
  NL: 'emea',
  NZ: 'japa',
  SA: 'emea',
  SE: 'emea',
  SG: 'japa',
  TW: 'japa',
  US: 'us',
  XA: 'emea',
  XB: 'emea',
  BE: 'emea',
  NO: 'emea',
  CH: 'emea',
  TH: 'japa',
  ID: 'japa',
};
const regex = /[a-z]{2}-[A-Za-z]{2}/;
export function getCountryCodeFromLocale(locale) {
  const isValidLocaleFormat = regex.test(locale);
  let country = null;

  if (isValidLocaleFormat) {
    const { length } = locale;
    const pos = locale.indexOf('-');
    // eslint-disable-next-line unicorn/prefer-string-slice -- stupid rule
    country = locale.substring(pos + 1, length).toUpperCase();
  }

  return country;
}

export function getRegionFromLocale(locale) {
  return countryToRegionMap[getCountryCodeFromLocale(locale)];
}
